import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const isMounted = useRef(true);
  const [totalStats, setTotalStats] = useState({ all: 0, new: 0, processed: 0, archived: 0 });

  const fetchTotalStats = useCallback(async () => {
    try {
      // Add timeout to prevent hanging
      const statsPromise = (async () => {
        const { count: allCount } = await supabase.from('applications').select('*', { count: 'exact', head: true });
        const { count: newCount } = await supabase.from('applications').select('*', { count: 'exact', head: true }).eq('status', 'new');
        const { count: processedCount } = await supabase.from('applications').select('*', { count: 'exact', head: true }).eq('status', 'processed');
        const { count: archivedCount } = await supabase.from('applications').select('*', { count: 'exact', head: true }).eq('status', 'archived');
        
        return { allCount, newCount, processedCount, archivedCount };
      })();
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Stats timeout')), 10000)
      );
      
      const { allCount, newCount, processedCount, archivedCount } = await Promise.race([statsPromise, timeoutPromise]);
      
      if (isMounted.current) {
        setTotalStats({
          all: allCount || 0,
          new: newCount || 0,
          processed: processedCount || 0,
          archived: archivedCount || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase.from('applications').select('*, tours(title)').order('created_at', { ascending: false });
      if (filter !== 'all') query = query.eq('status', filter);
      
      // Add timeout
      const fetchPromise = query;
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Fetch timeout')), 10000)
      );
      
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (error) throw error;
      if (isMounted.current) setApplications(data || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      if (isMounted.current) {
        setError(err.message);
        setApplications([]); // Set empty array on error
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    isMounted.current = true;
    fetchTotalStats();
    fetchApplications();
    
    const subscription = supabase
      .channel('applications-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'applications' }, () => {
        fetchTotalStats();
        fetchApplications();
      })
      .subscribe();
    
    return () => {
      isMounted.current = false;
      subscription.unsubscribe();
    };
  }, [fetchApplications, fetchTotalStats]);

  const updateStatus = async (id, status) => {
    try {
      const { error } = await supabase.from('applications').update({ status }).eq('id', id);
      if (error) throw error;
      fetchTotalStats();
      fetchApplications();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Ошибка при обновлении статуса: ' + err.message);
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Удалить заявку? Это действие нельзя отменить.')) return;
    try {
      const { error } = await supabase.from('applications').delete().eq('id', id);
      if (error) throw error;
      fetchTotalStats();
      fetchApplications();
    } catch (err) {
      console.error('Error deleting application:', err);
      alert('Ошибка при удалении заявки: ' + err.message);
    }
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      const exportData = applications.map(app => ({
        'Дата': new Date(app.created_at).toLocaleString('ru-RU'),
        'Имя': app.name,
        'Телефон': app.phone,
        'Тур': app.tour_name || (app.tours?.title) || 'Не указан',
        'Страница': app.page_url || 'Не указана',
        'Статус': app.status === 'new' ? 'Новая' : app.status === 'processed' ? 'Обработана' : 'В архиве',
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Заявки');
      XLSX.writeFile(wb, `заявки_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Ошибка при экспорте: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'new': return { backgroundColor: '#fef3c7', color: '#92400e', borderLeft: '3px solid #f59e0b' };
      case 'processed': return { backgroundColor: '#d1fae5', color: '#065f46', borderLeft: '3px solid #10b981' };
      default: return { backgroundColor: '#f3f4f6', color: '#374151', borderLeft: '3px solid #9ca3af' };
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'new': return 'Новая';
      case 'processed': return 'Обработана';
      default: return 'В архиве';
    }
  };

  if (loading && applications.length === 0) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#6b7280' }}>Загрузка заявок...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626', backgroundColor: '#fee2e2', borderRadius: '0.5rem', margin: '1rem' }}>
        <p>Ошибка загрузки заявок: {error}</p>
        <button onClick={() => { fetchTotalStats(); fetchApplications(); }} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
          Повторить попытку
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          Заявки
          <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>(всего: {totalStats.all})</span>
        </h1>
        <button onClick={exportToExcel} disabled={exporting || applications.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: applications.length === 0 ? 'not-allowed' : 'pointer', opacity: applications.length === 0 ? 0.5 : 1 }}>
          📥 {exporting ? 'Экспорт...' : 'Экспорт в Excel'}
        </button>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', cursor: 'pointer', border: filter === 'all' ? '2px solid #3b82f6' : '1px solid #e5e7eb' }} onClick={() => setFilter('all')}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>{totalStats.all}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Всего</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', cursor: 'pointer', border: filter === 'new' ? '2px solid #f59e0b' : '1px solid #e5e7eb' }} onClick={() => setFilter('new')}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>{totalStats.new}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Новые</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', cursor: 'pointer', border: filter === 'processed' ? '2px solid #10b981' : '1px solid #e5e7eb' }} onClick={() => setFilter('processed')}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{totalStats.processed}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Обработанные</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', cursor: 'pointer', border: filter === 'archived' ? '2px solid #6b7280' : '1px solid #e5e7eb' }} onClick={() => setFilter('archived')}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6b7280' }}>{totalStats.archived}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Архив</div>
        </div>
      </div>
      
      <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Дата</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Имя</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Телефон</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Тур</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Страница</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Статус</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr key={app.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{new Date(app.created_at).toLocaleString('ru-RU')}</td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{app.name}</td>
                <td style={{ padding: '0.75rem 1rem' }}><a href={`tel:${app.phone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{app.phone}</a></td>
                <td style={{ padding: '0.75rem 1rem' }}>{app.tour_name || app.tours?.title || '-'}</td>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={app.page_url}>{app.page_url || '-'}</td>
                <td style={{ padding: '0.75rem 1rem' }}><span style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500', ...getStatusStyle(app.status) }}>{getStatusText(app.status)}</span></td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    {app.status === 'new' && <button onClick={() => updateStatus(app.id, 'processed')} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem' }}>✓ Обработать</button>}
                    {app.status !== 'archived' && <button onClick={() => updateStatus(app.id, 'archived')} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem' }}>📦 Архив</button>}
                    {app.status === 'archived' && <button onClick={() => updateStatus(app.id, 'processed')} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem' }}>↺ Восстановить</button>}
                    <button onClick={() => deleteApplication(app.id)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem' }}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {applications.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', backgroundColor: '#f9fafb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
            <p>Нет заявок в этом разделе</p>
            {filter !== 'all' && <button onClick={() => setFilter('all')} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>Показать все заявки</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Applications;